<template>
  <div class="layer1">
    <p class="title">
      {{ cellType }}
    </p>
    <div
      ref="gridWrapper"
      class="layer2"
    >
      <div
        v-if="cells.length"
        class="cell-grid-window"
      >
        <div :style="{ height: `${topSpacerHeight}px` }" />
        <div
          class="cell-grid"
          :style="gridStyle"
        >
          <div
            v-for="(item, index) in visibleCells"
            :key="item.tile_id || `${cellType}-${index}`"
            class="cell-grid-item"
            :style="itemWrapperStyle"
          >
            <cell-image
              :tile-id="item.tile_id"
              :cell-type="cellType"
              :cell-tag="item.tag ?? ''"
              :img-path="item.img_url"
              :bbox="item.bounding_boxes"
              :img-width="imgWidth"
              :is-z-pressing="isZPressing"
              :show-bbox="showBbox"
              :show-normal="showNormal"
              :show-wrong="showWrong"
              :show-excluded="showExcluded"
              :drawing-mode="drawingMode"
              v-bind="$attrs"
            />
          </div>
        </div>
        <div :style="{ height: `${bottomSpacerHeight}px` }" />
      </div>
      <div
        v-else
        class="cell-grid-empty"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import CellImage from '@/views/components/CellImage.vue';
import { useCellGroupStore } from '@/store';
import type { CellBoundingBox } from '@/types/api';
import type { CheckboxValue } from 'bootstrap-vue-next';
import { useWindowedGrid } from '@/composables/useWindowedGrid';

const CELL_HORIZONTAL_PADDING = 20;
const CELL_VERTICAL_PADDING = 20;
const CELL_INFO_HEIGHT = 48;
const OVERSCAN_ROWS = 2;

const props = defineProps<{
  cellType: string;
  imgWidth: number;
  isZPressing?: boolean;
  showBbox?: boolean | CheckboxValue | CheckboxValue[];
  showNormal?: boolean | CheckboxValue | CheckboxValue[];
  showWrong?: boolean | CheckboxValue | CheckboxValue[];
  showExcluded?: boolean | CheckboxValue | CheckboxValue[];
  drawingMode?: boolean | CheckboxValue | CheckboxValue[];
}>();

const cellGroups = useCellGroupStore();
const gridWrapper = ref<HTMLElement | null>(null);

const cells = computed<CellBoundingBox[]>(() => {
  const list = cellGroups.groupDict?.[props.cellType];
  return Array.isArray(list) ? list : [];
});

const itemSecondarySize = computed(() => Math.round(props.imgWidth + (CELL_HORIZONTAL_PADDING * 2)));
const itemSize = computed(() => Math.round(props.imgWidth + (CELL_VERTICAL_PADDING * 2) + CELL_INFO_HEIGHT));
const itemWrapperStyle = computed(() => ({
  width: `${itemSecondarySize.value}px`,
  height: `${itemSize.value}px`,
  padding: `${CELL_VERTICAL_PADDING}px ${CELL_HORIZONTAL_PADDING}px`,
}));
const gridStyle = computed(() => ({
  gridTemplateColumns: `repeat(auto-fill, ${itemSecondarySize.value}px)`,
}));

const {
  visibleItems: visibleCells,
  topSpacerHeight,
  bottomSpacerHeight,
} = useWindowedGrid({
  items: cells,
  containerRef: gridWrapper,
  itemSecondarySize,
  rowHeight: itemSize,
  overscanRows: OVERSCAN_ROWS,
});
</script>

<style lang="scss" scoped>
.layer1 {
  border-radius: 5px;
  box-shadow: 0px 3px 20px 0px rgba(0, 0, 0, 0.1);
  background-color: white;
  padding: 43px 53px;
  margin-bottom: 20px;
  margin-left: 60px;
  margin-right: 60px;
}
.layer2 {
  background: #F4F6F9;
  border: 1px solid #E3E8EF;
  box-sizing: border-box;
  border-radius: 5px;
  padding: 10px 9px;
  position: relative;
  content-visibility: auto;
  overflow-anchor: none;
}
.title {
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0em;
  text-align: left;
  color: #212121;
}
.cell-grid-window {
  position: relative;
}
.cell-grid {
  position: relative;
  display: grid;
  justify-content: center;
}

.cell-grid-item {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

:deep(.cell-grid-item .canvas-wrapper) {
  margin: 0;
}

:deep(.cell-grid-item .tile-info) {
  width: 100%;
  text-align: center;
}

.cell-grid-empty {
  min-height: 60px;
}
</style>
